import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@nextui-org/react';
import { useGetPronunciationDictionariesQuery } from '@circuitry-ai/doc-data';
import { ErrorBoundary } from '@circuitry-ai/doc-ui';
import DictionarySidebar from './DictionarySidebar';
import DictionaryDetailPanel from './DictionaryDetailPanel';
import CreateDictionaryModal from './CreateDictionaryModal';

const CreateDictionaryModalWithErrorBoundary = ErrorBoundary(CreateDictionaryModal);
const DictionaryDetailPanelWithErrorBoundary = ErrorBoundary(DictionaryDetailPanel);

export const DictionaryTab: React.FC = () => {
  const { t } = useTranslation();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPerPage, setListPerPage] = useState(10);

  const { data: listResponse, isLoading, isError, error, refetch, isFetching } =
    useGetPronunciationDictionariesQuery({ page: listPage, per_page: listPerPage });

  const dictionaries = listResponse?.data ?? [];
  const listTotalCount = listResponse?.total_count ?? 0;

  const sortedDictionaries = useMemo(
    () =>
      [...dictionaries].sort((a, b) =>
        String(a.name ?? '').localeCompare(String(b.name ?? ''))
      ),
    [dictionaries]
  );

  useEffect(() => {
    if (!sortedDictionaries.length) {
      setSelectedName(null);
      return;
    }
    const stillExists = selectedName
      ? sortedDictionaries.some((dict) => dict.name === selectedName)
      : false;
    if (!selectedName || !stillExists) {
      setSelectedName(String(sortedDictionaries[0].name ?? '').trim() || null);
    }
  }, [selectedName, sortedDictionaries]);

  const handleDictionaryCreated = useCallback(
    (dictionaryName: string) => {
      setSelectedName(dictionaryName);
      setListPage(1);
      void refetch();
    },
    [refetch]
  );

  const handleListPerPageChange = useCallback((perPage: number) => {
    setListPerPage(perPage);
  }, []);

  const handleDictionaryDeleted = useCallback(() => {
    setSelectedName(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-1 min-h-[320px] items-center justify-center gap-2 rounded-lg border border-divider bg-default-50">
        <Spinner size="sm" />
        <span className="text-default-500 text-sm">{t('Loading pronunciations...')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-5 p-4 bg-default-50/50 rounded-lg overflow-auto">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">{t('Pronunciations')}</h2>
        <p className="text-sm text-default-500 mt-1 max-w-3xl">
          {t(
            'Manage pronunciation dictionaries that shape how words are spoken by AI workers across your tenant.'
          )}
        </p>
      </div>

      {isError && (
        <div className="text-sm text-danger rounded-lg border border-danger/30 bg-danger/10 px-3 py-2">
          {(error as { data?: { message?: string } })?.data?.message ||
            (error as Error)?.message ||
            t('Failed to load pronunciation dictionaries.')}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 min-h-[420px]">
        <DictionarySidebar
          dictionaries={sortedDictionaries}
          selectedName={selectedName}
          isLoading={isLoading || isFetching}
          onSelect={setSelectedName}
          onCreate={() => setCreateModalOpen(true)}
          page={listPage}
          perPage={listPerPage}
          totalCount={listTotalCount}
          onPageChange={setListPage}
          onPerPageChange={handleListPerPageChange}
        />

        {selectedName ? (
          <DictionaryDetailPanelWithErrorBoundary
            dictionaryName={selectedName}
            onDeleted={handleDictionaryDeleted}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-divider bg-content1 px-6 py-12 text-center text-sm text-default-500">
            {t('Select a dictionary from the list or create a new one.')}
          </div>
        )}
      </div>

      {createModalOpen && (
        <CreateDictionaryModalWithErrorBoundary
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleDictionaryCreated}
        />
      )}
    </div>
  );
};

export default DictionaryTab;
